import { api } from '@techsofit/app-kit';

/** v2 passwordless account auth — one email, many contexts (Slack-style). */

export type ProfileKind = 'INDIVIDUAL' | 'PLATFORM' | 'MEMBERSHIP' | 'STAFF';

export interface ProfileView {
  id: string;
  kind: ProfileKind;
  orgId: string | null;
  orgName: string | null;
  /** The gym's subdomain slug (null for personal/platform contexts). */
  slug: string | null;
  role: string;
  coach: boolean;
  current: boolean;
}

export interface AccountMe {
  accountId: string;
  email: string;
  fullName: string;
  platformAdmin: boolean;
  activeProfileId: string;
  profiles: ProfileView[];
  /** True only on the verify that created the account (first sign-in). */
  created: boolean;
}

/** Public branding for the current gym subdomain, used to skin the sign-in screen. */
export interface TenantBranding {
  slug: string;
  name: string;
  type: string;
  status: string;
  logoUrl: string | null;
  /** Whether the gym has the guest-pass feature on (gates the member "invite a friend" UI). */
  guestPassEnabled: boolean;
}

/** Resolve the gym for the current subdomain, or null on the apex / unknown host. */
export const fetchTenant = (): Promise<TenantBranding | null> =>
  api<TenantBranding>('/public/tenant').catch(() => null);

export const requestCode = (email: string, signup = false): Promise<{ otpSent: boolean }> =>
  api('/account-auth/request-code', { method: 'POST', body: { email, signup } });

export const verifyCode = (email: string, code: string, fullName?: string): Promise<AccountMe> =>
  api('/account-auth/verify-code', { method: 'POST', body: { email, code, fullName } });

/** One-tap sign-in from the email button (`?lt=` token). Single use. */
export const verifyLink = (token: string): Promise<AccountMe> =>
  api('/account-auth/verify-link', { method: 'POST', body: { token } });

export const fetchAccountMe = (): Promise<AccountMe> => api('/account-auth/me');

export const switchProfile = (profileId: string): Promise<AccountMe> =>
  api('/account-auth/switch', { method: 'POST', body: { profileId } });

export const setAccountName = (fullName: string): Promise<AccountMe> =>
  api('/account-auth/name', { method: 'POST', body: { fullName } });

export type OrgType = 'GYM' | 'SOLO_TRAINER';

export const createOrg = (name: string, type: OrgType, slug?: string): Promise<AccountMe> =>
  api('/account-auth/orgs', { method: 'POST', body: { name, type, slug } });

/** Live availability for the chosen subdomain at signup. */
export interface SlugAvailability {
  slug: string;
  available: boolean;
  reason: string | null;
  suggestion: string | null;
}
export const checkSlug = (slug: string): Promise<SlugAvailability> =>
  api(`/account-auth/slug-available?slug=${encodeURIComponent(slug)}`);

export const accountLogout = (): Promise<void> => api('/account-auth/logout', { method: 'POST' });
